import { z } from 'zod'
import { centimesLus, texteLu } from './dougs'
import { nommeLeFournisseur, SIGNES_MIN_NUMERO_JUSTIFICATIF } from './justificatifs'
import { normaliserLibelle } from './libelle'

// Constat des factures d'achat lues chez Dougs par une session Claude, avec le connecteur Dougs
// (outil list-vendor-invoices) : la facture dit où elle est rattachée, ce que l'adaptateur de
// l'agent du Mac ne lit pas (il ne compte que les pièces jointes d'une opération). Rend, pour
// chaque facture, la pièce de l'appli qu'elle est sans doute possible, pour la marquer « déjà chez
// le cabinet » (N0). Fonction pure : ni réseau, ni écriture chez Dougs. L'absence d'une facture
// ne prouve rien : on ne conclut que de ce qui est lu, une liste partielle convient donc.
//
// Ce qui est reçu est une donnée non fiable : formes revalidées, textes nettoyés et bornés.

/** Factures reçues au plus par constat : une page de list-vendor-invoices. */
export const FACTURES_CONSTAT_MAX = 500

/** Une facture telle que la rend list-vendor-invoices ; les autres clés sont ignorées. */
export const factureDougsLue = z.looseObject({
  id: z.string().min(1).max(64),
  /** Instant ISO ; null tant que Dougs n'a pas lu le document. */
  date: z.string().max(40).nullable(),
  /** Total TTC, toujours positif, dans la devise de la facture. */
  amount: z.number().nonnegative().nullable(),
  currency: z.string().max(8).nullable(),
  /** Un avoir : positif ici, négatif dans l'appli. */
  isRefund: z.boolean(),
  reference: z.string().max(200).nullable(),
  label: z.string().max(400).nullable(),
  supplierName: z.string().max(400).nullable(),
  /** Réellement rattachée ; une suggestion de Dougs (`suggestedMatch`) n'en est pas une. */
  isReconciled: z.boolean(),
  reconciledOperationIds: z.array(z.number().int().positive()).max(200),
})
export type FactureDougsLue = z.output<typeof factureDougsLue>

/** L'argument `constat` de l'outil MCP `collectes` : `dossier` est le `companyId` lu chez Dougs. */
export const constatCabinetSaisie = z.object({
  dossier: z.string().regex(/^[1-9]\d{0,15}$/),
  factures: z.array(factureDougsLue).max(FACTURES_CONSTAT_MAX),
  ecrire: z.boolean().optional(),
})

/** Ce que le constat lit d'une pièce (`PieceCandidate`). */
export type PieceDuConstat = {
  id: string
  numero: string | null
  montantsCts: readonly number[]
  devise: string | null
  noms: readonly string[]
  libellesBancaires: readonly string[]
}

/** La facture, rendue avec des textes nettoyés et son montant en centimes. */
export type FactureConstatee = {
  id: string
  date: string | null
  montantCts: number | null
  devise: string | null
  reference: string | null
  fournisseur: string | null
}

export type IssueConstatDougs =
  | { facture: FactureConstatee; issue: 'rattachee'; pieceId: string; operation: string }
  /** Chez Dougs, rattachée à rien : la pièce y est, l'opération reste à relier chez Dougs. */
  | { facture: FactureConstatee; issue: 'sans_operation'; pieceId: string }
  /** Répartie sur plusieurs opérations : à regarder, un seul marquage ne le dirait pas. */
  | { facture: FactureConstatee; issue: 'plusieurs_operations'; pieceId: string }
  | { facture: FactureConstatee; issue: 'ambigue'; pieceIds: string[] }
  /** Chez Dougs, sans pièce dans l'appli. */
  | { facture: FactureConstatee; issue: 'sans_piece' }
  /** Sans pièce, date ou montant illisibles : rien n'en est conclu. */
  | { facture: FactureConstatee; issue: 'illisible' }

export type ConstatDougs = {
  issues: IssueConstatDougs[]
  /** Factures sans pièce d'avant l'exercice : gardées chez Dougs, sans suivi. */
  anciennes: number
}

const TEXTE_MAX = 200

const mots = (t: string) =>
  normaliserLibelle(t)
    .split(/[^A-Z0-9]+/)
    .filter(Boolean)

/**
 * Le numéro de la pièce est la référence de la facture (forme compacte), ou la fin de son nom, à
 * une frontière de mot : l'appli nomme ses dépôts « …_FACTURE-<numéro> », et la référence lue par
 * Dougs peut différer du numéro de la pièce.
 */
function numeroConcorde(numero: string, f: FactureDougsLue): boolean {
  const cherche = mots(numero)
  if (cherche.length === 0) return false
  if (f.reference !== null && mots(f.reference).join('') === cherche.join('')) return true
  if (f.label === null) return false
  const fin = mots(f.label.replace(/\.[a-z0-9]{2,5}$/i, '')).slice(-cherche.length)
  return fin.length === cherche.length && fin.every((m, i) => m === cherche[i])
}

function concorde(f: FactureDougsLue, montantCts: number, p: PieceDuConstat): boolean {
  if (p.numero === null || !numeroConcorde(p.numero, f)) return false
  if (p.devise !== null && f.currency?.trim().toUpperCase() !== p.devise) return false
  if (!p.montantsCts.some((m) => Math.abs(m) === montantCts && m < 0 === f.isRefund)) return false
  // Un numéro court se retrouve chez d'autres : le fournisseur doit concorder aussi.
  if (mots(p.numero).join('').length >= SIGNES_MIN_NUMERO_JUSTIFICATIF) return true
  const nom = f.supplierName ?? ''
  return nom !== '' && nommeLeFournisseur({ libelle: nom, marchand: nom }, p, 'large')
}

const jourLu = (date: string | null) => {
  const r = z.iso.date().safeParse(date?.slice(0, 10))
  return r.success ? r.data : null
}

/**
 * Une issue par facture, une seule fois chacune, dans l'ordre reçu. Une pièce n'est retenue que
 * seule candidate de sa facture, et sa facture seule candidate de la pièce : même numéro, même
 * montant au signe près, même sens (avoir ou non), même devise. La date de Dougs ne décide jamais
 * d'un appariement (sa lecture se trompe parfois d'année, et la pièce serait redéposée) : elle ne
 * sert qu'à taire une facture sans pièce d'avant l'exercice (`depuis`).
 */
export function constatFacturesDougs(
  factures: readonly FactureDougsLue[],
  pieces: readonly PieceDuConstat[],
  o: { depuis: string },
): ConstatDougs {
  const uniques = [...new Map(factures.map((f) => [f.id, f])).values()]
  const enVue = uniques.map((f) => {
    const montantCts = f.amount === null ? null : centimesLus(f.amount)
    const vue: FactureConstatee = {
      id: f.id,
      date: jourLu(f.date),
      montantCts,
      devise: texteLu(f.currency, 8),
      reference: texteLu(f.reference, TEXTE_MAX),
      fournisseur: texteLu(f.supplierName, TEXTE_MAX),
    }
    const candidates =
      montantCts === null ? [] : pieces.filter((p) => concorde(f, montantCts, p)).map((p) => p.id)
    return { f, vue, candidates }
  })
  const estAncienne = ({ vue, candidates }: (typeof enVue)[number]) =>
    candidates.length === 0 && vue.date !== null && vue.date < o.depuis
  const suivies = enVue.filter((e) => !estAncienne(e))
  const usages = new Map<string, number>()
  for (const { candidates } of suivies)
    for (const id of candidates) usages.set(id, (usages.get(id) ?? 0) + 1)
  const issues = suivies.map(({ f, vue, candidates }): IssueConstatDougs => {
    const [pieceId] = candidates
    if (pieceId === undefined)
      return {
        facture: vue,
        issue: vue.date === null || vue.montantCts === null ? 'illisible' : 'sans_piece',
      }
    if (candidates.length > 1 || usages.get(pieceId) !== 1)
      return { facture: vue, issue: 'ambigue', pieceIds: candidates }
    const [operation, ...autres] = f.reconciledOperationIds
    if (!f.isReconciled || operation === undefined)
      return { facture: vue, issue: 'sans_operation', pieceId }
    if (autres.length > 0) return { facture: vue, issue: 'plusieurs_operations', pieceId }
    return { facture: vue, issue: 'rattachee', pieceId, operation: String(operation) }
  })
  return { issues, anciennes: enVue.length - suivies.length }
}
